import React, { useState, useEffect } from "react";
import css from "./teacher_side_menu.module.scss";
import classNames from "classnames";
import * as Icons from "Icons";
import { IconSettings, IconLogout, IconChevronRight, IconElective, IconBotMenu, IconOldCourses } from "Icons";
import { NavLink, useLocation, useHistory } from "react-router-dom";
import { withApiContext, withLabelsContext, withUserContext, withAccessContext, withWindowContext } from "context";
import * as util from "services/util";
import * as config from "config";
import * as messages from "messages";
import Loader from "components/Spinner/Loader";
import avatar from "./images/avatar.png";
import Cookies from "js-cookie";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Collapse } from "react-bootstrap";
import PermanentDrawerWrapper from "re_redesigned_componnents/drawer/PermanentDrawerWrapper";

const callShibbolethLogout = () => {
  const sessionKey = Object.keys(Cookies.get()).find((k) =>
    k.match(/^_shibsession_.*$/)
  );
  const sessionValue = Cookies.get(sessionKey);
  return new Promise((_resolve, _reject) => {
    let sessionHeaders = new Headers();
    sessionHeaders.append("Cookie", `${sessionKey}=${sessionValue}`);
    let sessionOptions = {
      method: "GET",
      headers: sessionHeaders,
      redirect: "follow",
    };
    fetch(config.insLinks.logout, sessionOptions).then((data) => {
      console.log(data);
    });
  });
};

function TeacherSideMenu(props) {
  const location = useLocation();
  const history = useHistory();
  const currentUser = props.ctx_user.user || {};
  const userName = currentUser.firstName || currentUser.name || "";
  const className = currentUser.className || "";

  // Profile photo logic (same as UserProfile.jsx)
  const photo = currentUser.photo
    ? currentUser.photo.match(/^data:image\/\w+;base64,/) !== null
      ? currentUser.photo
      : `data:image/png;base64,${currentUser.photo}`
    : avatar;

  // Menu data
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenus, setOpenMenus] = useState({});

  // Load menu from sidenavData prop (same data source as Sidenav.jsx)
  useEffect(() => {
    if (props.sidenavData && props.sidenavData.length > 0) {
      setMenuItems(props.sidenavData);
      setLoading(false);
    }
  }, [props.sidenavData]);

  useEffect(() => {
    if (props.sidenavLoading !== undefined) {
      setLoading(props.sidenavLoading);
    }
  }, [props.sidenavLoading]);

  const makeMenuLabel = (k) =>
    util.capitalizeWord(props.ctx_labels.singular[k] || k);

  const toggleSubmenu = (menuId) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  // Logout handler (same logic as LogoutButton.jsx)
  const handleLogOut = () => {
    props.ctx_window.simpleConfirm(
      messages.mkLogoutConfirm(props.ctx_labels.singular.logoutConfirm),
      (allow) => {
        if (allow) {
          if (currentUser.analytics && currentUser.analytics.isAnalyticsTracking) {
            props
              .onLogout()
              .then(() => finalizeLogout())
              .catch(() => finalizeLogout());
          } else {
            finalizeLogout();
          }
        }
      }
    );
  };

  const finalizeLogout = () => {
    props.ctx_access.logOut();
    currentUser.loginType === "sso" && callShibbolethLogout();
    history.push(
      currentUser.loginType === "sso"
        ? config.insLinks.signout
        : util.getHomeUrl(null)
    );
  };

  // Separate parent and child menu items
  const parentMenuItems = menuItems.filter(
    (mn) => mn.parent_id === 0 && mn.hidden !== 1
  );
  const childMenuItems = menuItems.filter((m) => m.parent_id !== 0);

  // Check if a route is currently active
  const isRouteActive = (route) => {
    if (!route) return false;
    return location.pathname === String(route).trim();
  };

  // Render a single menu item (no children)
  const renderSimpleMenuItem = (menuItem) => {
    const icon = menuItem.logo;
    const Icon = Icons[icon];
    const isActive = isRouteActive(menuItem.route);

    // Special cases: Subjects, Old Courses, Shelf, Bots
    if (menuItem.screen_id === config.memberSubmenuE.SUBJECT) {
      return (
        <li key={menuItem.menu_id} id={menuItem.menu_name}>
          <div
            className={css.menu_link}
            id={props.isSubjectsMenuOpen ? css.active_font : undefined}
            onClick={props.toggleSubjectsMenu}
          >
            <div className={classNames(css.active_indicator, props.isSubjectsMenuOpen && css.active_indicator_show)}></div>
            <div className={css.menu_icon}><IconElective /></div>
            {makeMenuLabel(menuItem.menu_name)}
            <div className={css.chevron}>
              <IconChevronRight width="12" />
            </div>
          </div>
        </li>
      );
    }

    if (menuItem.screen_id === config.memberSubmenuE.OLD_COURSES || currentUser.enableOldCourses) {
      return (
        <li key={menuItem.menu_id} id={menuItem.menu_name}>
          <div
            className={css.menu_link}
            id={props.isOldCoursesMenuOpen ? css.active_font : undefined}
            onClick={props.toggleOldCoursesMenu}
          >
            <div className={classNames(css.active_indicator, props.isOldCoursesMenuOpen && css.active_indicator_show)}></div>
            <div className={css.menu_icon}><IconOldCourses /></div>
            {makeMenuLabel(menuItem.menu_name)}
            <div className={css.chevron}>
              <IconChevronRight width="12" />
            </div>
          </div>
        </li>
      );
    }

    if (menuItem.screen_id === config.memberSubmenuE.BOTS) {
      return (
        <li key={menuItem.menu_id} id={menuItem.menu_name}>
          <div
            className={css.menu_link}
            id={props.isBotMenuOpen ? css.active_font : undefined}
            onClick={props.toggleBotMenu}
          >
            <div className={classNames(css.active_indicator, props.isBotMenuOpen && css.active_indicator_show)}></div>
            <div className={css.menu_icon}><IconBotMenu /></div>
            {makeMenuLabel(menuItem.menu_name)}
            <div className={css.chevron}>
              <IconChevronRight width="12" />
            </div>
          </div>
        </li>
      );
    }

    if (menuItem.screen_id === config.memberSubmenuE.STUDENT_VIEWSHELF) {
      return (
        <li key={menuItem.menu_id} id={menuItem.menu_name}>
          <div
            className={css.menu_link}
            id={props.isViewShelfMenuOpen ? css.active_font : undefined}
            onClick={props.toggleShelfMenu}
          >
            <div className={classNames(css.active_indicator, props.isViewShelfMenuOpen && css.active_indicator_show)}></div>
            <div className={css.menu_icon}><IconElective /></div>
            {makeMenuLabel(menuItem.menu_name)}
            <div className={css.chevron}>
              <IconChevronRight width="12" />
            </div>
          </div>
        </li>
      );
    }

    // Regular menu item
    return (
      <li key={menuItem.menu_id} id={menuItem.menu_name}>
        <NavLink
          exact
          to={String(menuItem.route?.trim())}
          onClick={() => props.onMenuSelect(menuItem.menu_id)}
          className={css.menu_link}
          style={{ textDecoration: "none" }}
        >
          <div className={classNames(css.active_indicator, isActive && css.active_indicator_show)}></div>
          {Icon && <div className={css.menu_icon}><Icon /></div>}
          <span className={isActive ? css.active_font : undefined}>
            {makeMenuLabel(menuItem.menu_name)}
          </span>
        </NavLink>
      </li>
    );
  };

  // Render a menu item with children (submenu)
  const renderParentMenuItem = (menuItem) => {
    const icon = menuItem.logo;
    const Icon = Icons[icon];
    const children = childMenuItems.filter(
      (sub) => menuItem.menu_id === sub.parent_id && sub.hidden !== 1
    );
    const isOpen = openMenus[menuItem.menu_id] || false;

    // Check if any child route is active
    const anyChildActive = children.some((child) => isRouteActive(child.route));

    return (
      <li key={menuItem.menu_id} id={menuItem.menu_name}>
        <div
          className={css.menu_link}
          id={anyChildActive ? css.active_font : undefined}
          onClick={() => toggleSubmenu(menuItem.menu_id)}
        >
          <div className={classNames(css.active_indicator, anyChildActive && css.active_indicator_show)}></div>
          {Icon && <div className={css.menu_icon}><Icon /></div>}
          {makeMenuLabel(menuItem.menu_name)}
          <div className={classNames(css.chevron, isOpen && css.chevron_open)}>
            <KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#b1b1b1' }} />
          </div>
        </div>
        <Collapse in={isOpen}>
          <div className={css.submenu_container}>
            {children.map((subMenuItem) => {
              const isSubActive = isRouteActive(subMenuItem.route);
              return (
                <NavLink
                  key={subMenuItem.menu_id}
                  exact
                  to={String(subMenuItem.route)}
                  onClick={() => props.onMenuSelect(subMenuItem.menu_id, subMenuItem.pb_report_id, subMenuItem.report_type)}
                  className={css.submenu_link}
                  style={{ textDecoration: "none" }}
                >
                  <span className={isSubActive ? css.active_font : undefined}>
                    {makeMenuLabel(subMenuItem.menu_name)}
                  </span>
                </NavLink>
              );
            })}
          </div>
        </Collapse>
      </li>
    );
  };

  return (
    <PermanentDrawerWrapper>
      <div className={css.wrapper}>
        {/* ── Profile Section (fixed at top) ── */}
        <div className={css.profile_section}>
          <NavLink to={config.ROUTES.profileSettings} style={{ textDecoration: "none" }}>
            <div style={{ marginBottom: "0.5rem" }}>
              <img className={css.profile_pic} src={photo} alt="" />
            </div>
            <div className={css.profile_name}>{userName}</div>
            {className && <div className={css.profile_role}>{className}</div>}
          </NavLink>
        </div>

        {/* ── Scrollable Nav Area ── */}
        <div className={css.nav_area}>
          {loading ? (
            <Loader active />
          ) : (
            <ul className={css.nav_list}>
              {parentMenuItems.map((menuItem) =>
                menuItem.has_children === 0
                  ? renderSimpleMenuItem(menuItem)
                  : renderParentMenuItem(menuItem)
              )}
              {/* Profile Settings link */}
              <li>
                <NavLink
                  exact
                  to={config.ROUTES.profileSettings}
                  onClick={() => props.profileclick1 && props.profileclick1(true)}
                  className={css.menu_link}
                  style={{ textDecoration: "none" }}
                >
                  <div className={classNames(css.active_indicator, isRouteActive(config.ROUTES.profileSettings) && css.active_indicator_show)}></div>
                  <div className={css.menu_icon}><IconSettings /></div>
                  <span className={isRouteActive(config.ROUTES.profileSettings) ? css.active_font : undefined}>
                    {props.ctx_labels.singular.profileSetting}
                  </span>
                </NavLink>
              </li>
            </ul>
          )}
        </div>

        {/* ── Logout Section (fixed at bottom) ── */}
        {props.showItems && (
          <div className={css.logout_section}>
            <button className={css.logout_btn} onClick={handleLogOut}>
              <IconLogout width="16" height="16" />
              <span>{props.ctx_labels.singular.logOut}</span>
            </button>
          </div>
        )}
      </div>
    </PermanentDrawerWrapper>
  );
}

export default withLabelsContext(
  withApiContext(
    withUserContext(
      withAccessContext(
        withWindowContext(util.withRouterHistory(TeacherSideMenu))
      )
    )
  )
);
